/* eslint-disable @typescript-eslint/no-explicit-any */
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useAuth, useVenueDetailsBySlug, useI18N } from "common/hooks";
import { useEffect, useState } from "react";
import { useGeoLocation } from "@/hooks/useGeoLocation";
import { defaultRegion } from "common/config";
import Loader from "@/components/common/Loader";
import { AuthModalScreen, useAuthModals } from "@/hooks/useAuthModals";
import { Helmet } from "react-helmet-async";
import { Storage } from "common/storage";
import { createPageAnalytics } from "common/analytics";
import { SlugRedirectError } from "common/apis/venueDetails";

const areaMapA = createPageAnalytics("area_map");
import WhatsNearbyContent from "../components/WhatsNearby/WhatsNearbyContent";
import { cn, getVenueSlugs } from "@/lib/utils";

export type WhatsNearbyRouteParams = {
  venueId: string; // from the route path"/whats-nearby/$venueId"
};

export type WhatsNearbyRouteSearch = {
  source?: "map" | "activity";
  showAuthModal?: boolean;
  cid?: string;
  tid?: string;
  venue?: string;
};

export const Route = createFileRoute("/whats-nearby/$venueId")({
  component: WhatsNearby,
  validateSearch: (search: Record<string, unknown>): WhatsNearbyRouteSearch => {
    return {
      source: search.source as "map" | "activity",
      showAuthModal: search.showAuthModal as boolean,
      cid: search.cid as string,
      tid: search.tid as string,
      venue: search.venue as string | undefined,
    };
  },
});

function WhatsNearby() {
  const { venueId: venueSlug } = Route.useParams();
  const { showAuthModal, cid, tid, venue } = Route.useSearch();
  const navigate = useNavigate();
  const i18n = useI18N();

  const { user, isLoading, isFetching } = useAuth();
  const isAuthLoading = isLoading || isFetching;

  const [showAuthModalState, setShowAuthModalState] = useState(showAuthModal);
  const { location } = useGeoLocation();

  // Redirect to venue details page if venue query param is present on page load
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const venueQuerySlug = urlParams.get("venue");
    if (venueQuerySlug) {
      // Redirect to the actual venue details page
      navigate({ to: `/venuedetails/${venueQuerySlug}` });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Empty dependency array - only run on mount

  // load this venue from the server
  const {
    data,
    error,
    refetch: loadVenueDetails,
    isLoading: isDetailsLoading,
  } = useVenueDetailsBySlug({
    slug: venueSlug,
    latitude:
      location?.latitude.toString() || defaultRegion.latitude.toString(),
    longitude:
      location?.longitude.toString() || defaultRegion.longitude.toString(),
    accessToken: Storage.getCurrentUserToken(),
    enabled: location ? true : false,
    type: "Complex",
    // Surface a retired-slug 301 as a SlugRedirectError so we can redirect the URL to
    // the canonical slug BEFORE the sync-spaces call runs. Without this the details
    // fetch silently follows the 301 but sync-spaces is still posted with the stale
    // slug and the backend answers "Business not found by slug."
    throwOnSlugRedirect: true,
  });

  const { showModal } = useAuthModals();

  useEffect(() => {
    if (location && !isAuthLoading) {
      loadVenueDetails();
    }
  }, [location, loadVenueDetails, user, isAuthLoading]);

  // Retired-slug 301: the details fetch reported that this slug now redirects to a new
  // canonical slug. Swap the URL to it (preserving search params) so every follow-up
  // slug-keyed call — sync-spaces, back-to-venue, etc. — uses the live slug. replace:true
  // keeps the dead slug out of history. Mirrors the venuedetails route's SSR 301.
  useEffect(() => {
    if (error instanceof SlugRedirectError) {
      // Preserve the current search params (cid/tid/source/…). Cast per the router's
      // typed-navigate limitation, matching the venuedetails route's 301 `as any`.
      navigate({
        to: `/whats-nearby/${error.canonicalSlug}`,
        search: (prev: WhatsNearbyRouteSearch) => prev,
        replace: true,
      } as any);
    }
  }, [error, navigate]);

  useEffect(() => {
    if (showAuthModalState && !user && !isAuthLoading) {
      showModal(AuthModalScreen.EnterEmail);
      setShowAuthModalState(false);
    }
  }, [showAuthModalState, user, isAuthLoading, showModal]);

  useEffect(() => {
    if (data?.details) {
      areaMapA.track("page_view_page", {
        area_map_name: data.details.name,
        results_count: 0,
      });
    }
  }, [data?.details, cid, tid]);

  return (
    <>      
      {(isAuthLoading || isDetailsLoading) && (
        <>
          <div role="status" aria-live="polite" className="sr-only">
            {i18n.get("loadingVenueDetails")}
          </div>
          <Loader />
        </>
      )}
      <main
        id="main"
        className="h-below-navbar  bg-bg-brand-secondary-default flex overflow-x-hidden w-full"
      >

        {/* Regular Desktop View - Always mounted, hidden when showing accessibility or photos detail */}
        <div
          className={cn(
            "md:flex flex-1 min-w-0",
            // (showAccessibilityDetail || showPhotosDetail) && "!hidden",
          )}
        >
          {error && !(error instanceof SlugRedirectError) && (
            <div className="max-w-[1160px] px-4 mx-auto overflow-hidden">
              <div role="alert">{error.message}</div>
            </div>
          )}

          {data && (
            <WhatsNearbyContent
              venueDetailsResponse={data}
              venueSlug={venueSlug}
              venueParam={venue}
              onClose={() => {
                const { venueDetailsSlug } = getVenueSlugs(data.details);
                window.location.href = `/venuedetails/${venueDetailsSlug}`;
              }}
              onRefresh={() => {
                setTimeout(() => loadVenueDetails(), 1000);
              }}
              cid={cid}
              tid={tid}
            />
          )}
        </div>
      </main>
    </>
  );
}
